"use client";
import * as Sentry from "@sentry/nextjs";
import type { Event } from "@sentry/nextjs";

// Initialize performance monitoring
export const initializeErrorTracking = () => {
  // Track unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    Sentry.captureException(event.reason, {
      tags: {
        type: "unhandled_promise_rejection",
      },
      extra: {
        reason: event.reason,
      },
    });
  });

  // Track uncaught exceptions
  window.addEventListener("error", (event) => {
    Sentry.captureException(event.error, {
      tags: {
        type: "uncaught_error",
      },
      extra: {
        message: event.message,
        filename: event.filename,
        lineNo: event.lineno,
        colNo: event.colno,
      },
    });
  });

  // Track network errors
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);

      // Track failed requests
      if (!response.ok) {
        const requestUrl =
          typeof args[0] === "string" ? args[0] : "unknown";
        Sentry.captureMessage(`HTTP ${response.status} for ${requestUrl}`, {
          level: "error",
          tags: {
            type: "network_error",
            status: response.status,
            url: typeof args[0] === "string" ? args[0] : "unknown",
          },
        });
      }

      return response;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          type: "fetch_error",
        },
        extra: {
          url: typeof args[0] === "string" ? args[0] : "unknown",
        },
      });
      throw error;
    }
  };

  // Track console errors
  const originalConsoleError = console.error;
  console.error = (...args) => {
    Sentry.captureException(args[0], {
      tags: {
        type: "console_error",
      },
      extra: {
        arguments: args,
      },
    });
    originalConsoleError.apply(console, args);
  };

  // Performance monitoring
  Sentry.addEventProcessor((event: Event) => {
    // Add performance data if available
    if (window.performance) {
      const performanceMetrics = {
        navigationTiming: performance.getEntriesByType("navigation")[0],
        resourceTiming: performance.getEntriesByType("resource"),
        paintTiming: performance.getEntriesByType("paint"),
      };

      event.extra = {
        ...event.extra,
        performance: performanceMetrics,
      };
    }
    return event;
  });

  // Track React render errors
  if (process.env.NODE_ENV === "development") {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (
        args[0]?.includes?.("React will try to recreate this component tree")
      ) {
        Sentry.captureException(new Error("React render error"), {
          tags: {
            type: "react_render_error",
          },
          extra: {
            message: args[0],
          },
        });
      }
      originalConsoleError.apply(console, args);
    };
  }
};
